/**
 * /quotes/catalogue: the products the company quotes from (docs/10 §3.1):
 * heat pumps (rated output, output at design temperature, SCOP at 35/55 °C…),
 * cylinders, buffers, controls, radiators and ancillaries, each with the
 * installer's COST price. Packages pick from here; the pricing model marks costs up.
 *
 * Where prices come from: UK merchants don't publish price APIs (docs/10 §9),
 * so the installer imports their merchant's price list (CSV), matched on the
 * supplier code then the manufacturer code, or edits prices here. A starter
 * catalogue of public merchant prices (each with its source link) gets them going.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Download, ExternalLink, PackagePlus, Pencil, Plus, Search, Trash2, Upload, X } from "lucide-react";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useQuotesApi, type Category, type Product } from "../../quotes/api";
import { CATEGORY_LABELS, gbp } from "../../quotes/format";
import "../../quotes/quotes.css";

const CATEGORIES = Object.keys(CATEGORY_LABELS) as Category[];

// CSV columns, in export order (import accepts any order, matched by header)
const COLUMNS: (keyof Product)[] = [
  "category",
  "brand",
  "model",
  "name",
  "sku",
  "mpn",
  "supplier",
  "costExVat",
  "kw",
  "designOutputKw",
  "scop35",
  "scop55",
  "refrigerant",
  "phase",
  "soundPowerDb",
  "litres",
  "datasheetUrl",
  "priceSourceUrl",
  "priceSeenAt",
];
const NUMERIC = new Set<keyof Product>(["costExVat", "kw", "designOutputKw", "scop35", "scop55", "soundPowerDb", "litres", "maxFlowC"]);

// Common merchant price-list headings → our fields
const HEADER_ALIASES: Record<string, keyof Product> = {
  "product code": "sku",
  code: "sku",
  "supplier code": "sku",
  sku: "sku",
  "manufacturer code": "mpn",
  "manufacturer part number": "mpn",
  mpn: "mpn",
  description: "name",
  "product name": "name",
  name: "name",
  price: "costExVat",
  "net price": "costExVat",
  "trade price": "costExVat",
  "your price": "costExVat",
  "cost ex vat": "costExVat",
  costexvat: "costExVat",
  brand: "brand",
  manufacturer: "brand",
  model: "model",
  category: "category",
  kw: "kw",
  litres: "litres",
};

/** A small RFC 4180 CSV parser (quotes, commas and newlines inside quotes). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") {
      row.push(cell);
      cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      if (row.some((x) => x.trim())) rows.push(row);
      row = [];
      cell = "";
    } else cell += c;
  }
  row.push(cell);
  if (row.some((x) => x.trim())) rows.push(row);
  return rows;
}

function toCsv(products: Product[]): string {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [COLUMNS.join(","), ...products.map((p) => COLUMNS.map((c) => esc(p[c])).join(","))].join("\n");
}

/** CSV rows → product rows for the import API (unknown columns ignored). */
function rowsFromCsv(text: string): Record<string, unknown>[] {
  const [head, ...body] = parseCsv(text);
  if (!head) return [];
  const fields = head.map((h) => {
    const k = h.trim().toLowerCase().replace(/[()£]/g, "").replace(/\s+/g, " ");
    return (COLUMNS as string[]).includes(h.trim()) ? (h.trim() as keyof Product) : HEADER_ALIASES[k];
  });
  return body.map((cells) => {
    const r: Record<string, unknown> = {};
    fields.forEach((f, i) => {
      if (!f) return;
      const raw = (cells[i] ?? "").trim();
      if (!raw) return;
      r[f] = NUMERIC.has(f) ? Number(raw.replace(/[£,\s]/g, "")) : raw;
    });
    if (!r.category) r.category = r.kw ? "heat_pump" : r.litres ? "cylinder" : "other";
    return r;
  });
}

const EMPTY: Partial<Product> = { category: "heat_pump", brand: "", model: "", name: "", sku: "", mpn: "", supplier: "", costExVat: 0, notes: "", datasheetUrl: "", priceSourceUrl: "", priceSeenAt: "", active: true, phase: "single" };

export function CataloguePage() {
  const api = useQuotesApi();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [category, setCategory] = useState<Category | "all">("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<Product> | null>(null);
  const [deleting, setDeleting] = useState<Product | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger" | "info"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .listProducts()
      .then(setProducts)
      .catch((e: Error) => setMessage({ tone: "danger", text: e.message }));
  }, [api]);

  const shown = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return (products ?? []).filter(
      (p) => (category === "all" || p.category === category) && words.every((w) => [p.name, p.brand, p.model, p.sku, p.mpn, p.supplier].join(" ").toLowerCase().includes(w)),
    );
  }, [products, category, search]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const p of products ?? []) c[p.category] = (c[p.category] ?? 0) + 1;
    return c;
  }, [products]);

  async function loadStarter() {
    setBusy(true);
    setMessage(null);
    try {
      const r = await api.loadStarter();
      setProducts(r.products);
      setMessage({ tone: "success", text: r.added ? `Added ${r.added} starter products and example packages. Check the prices against your merchant's.` : "The starter catalogue is already loaded." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function importFile(file: File) {
    setBusy(true);
    setMessage(null);
    try {
      const rows = rowsFromCsv(await file.text());
      if (!rows.length) throw new Error("That file has no rows. The first row must be the column headings.");
      const r = await api.importProducts(rows, true);
      setProducts(r.products);
      setMessage({
        tone: r.errors.length ? "info" : "success",
        text: `Imported: ${r.created} new, ${r.updated} updated.${r.errors.length ? ` ${r.errors.length} rows skipped: ${r.errors.slice(0, 3).map((e) => `row ${e.row + 1}: ${e.error}`).join("; ")}` : ""}`,
      });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function exportCsv() {
    const blob = new Blob([toCsv(products ?? [])], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "catalogue.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function toggle(p: Product) {
    try {
      const updated = await api.updateProduct({ ...p, active: !p.active });
      setProducts((ps) => (ps ?? []).map((x) => (x.productId === p.productId ? updated : x)));
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    }
  }

  return (
    <div className="page q-page">
      <div className="page-head q-head">
        <div>
          <h1>Catalogue</h1>
          <p>The products you quote with, at your cost price. Import your merchant's price list, or add and edit products here. Your packages and pricing models turn these into customer prices.</p>
        </div>
        <div className="q-head-actions">
          <button type="button" className="btn btn-secondary" onClick={() => fileRef.current?.click()} disabled={busy}>
            <Upload size={16} aria-hidden="true" /> Import price list
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setEditing({ ...EMPTY })}>
            <Plus size={16} aria-hidden="true" /> Add product
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => e.target.files?.[0] && void importFile(e.target.files[0])} />
        </div>
      </div>

      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}

      {products && products.length === 0 ? (
        <div className="card empty">
          <h2>Your catalogue is empty</h2>
          <p>
            Start with our starter catalogue: 40+ current heat pumps from nine brands (Samsung, Vaillant, Mitsubishi, Daikin, Grant, Viessmann, Ideal, LG, Panasonic), heat pump cylinders and the parts
            every install needs. Prices are public UK merchant prices (ex VAT) with their source, to replace with your own.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => void loadStarter()} disabled={busy}>
            <PackagePlus size={16} aria-hidden="true" /> {busy ? "Loading…" : "Load the starter catalogue"}
          </button>
          <p className="muted">Or import your merchant's price list as a CSV (columns like code, description, price, brand; see “Export CSV” for the full layout).</p>
        </div>
      ) : (
        <>
          <div className="dash-filters q-filters">
            <div className="ed-segmented" role="group" aria-label="Category">
              <button type="button" className={category === "all" ? "is-on" : ""} onClick={() => setCategory("all")}>
                All ({products?.length ?? 0})
              </button>
              {CATEGORIES.filter((c) => counts[c]).map((c) => (
                <button key={c} type="button" className={category === c ? "is-on" : ""} onClick={() => setCategory(c)}>
                  {CATEGORY_LABELS[c]} ({counts[c]})
                </button>
              ))}
            </div>
            <label className="icon-input q-search">
              <Search size={16} aria-hidden="true" />
              <input className="input" placeholder="Search name, brand or code" value={search} onChange={(e) => setSearch(e.target.value)} />
            </label>
          </div>
          <div className="card table-scroll">
            <table className="stats-table q-catalogue">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Codes</th>
                  <th className="num">Size</th>
                  <th className="num">SCOP 35/55</th>
                  <th className="num">Cost ex VAT</th>
                  <th>Price from</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.productId} className={p.active ? "" : "is-inactive"}>
                    <td>
                      <strong>{p.name}</strong>
                      <div className="muted">{CATEGORY_LABELS[p.category]}{p.brand ? ` · ${p.brand}` : ""}</div>
                    </td>
                    <td className="muted q-codes">{[p.sku && `Supplier ${p.sku}`, p.mpn && `Mfr ${p.mpn}`].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="num">
                      {p.kw ? `${p.kw} kW` : p.litres ? `${p.litres} L` : "—"}
                      {p.designOutputKw && p.designOutputKw !== p.kw ? <div className="muted">{p.designOutputKw} kW at design</div> : null}
                    </td>
                    <td className="num">{p.scop35 ? `${p.scop35} / ${p.scop55 ?? "—"}` : "—"}</td>
                    <td className="num">{gbp(p.costExVat)}</td>
                    <td className="muted">
                      {p.priceSourceUrl ? (
                        <a href={p.priceSourceUrl} target="_blank" rel="noreferrer" title={p.priceSourceUrl}>
                          {p.supplier || "source"} <ExternalLink size={11} aria-hidden="true" />
                        </a>
                      ) : (
                        p.supplier || "—"
                      )}
                      {p.priceSeenAt && <div>{p.priceSeenAt}</div>}
                    </td>
                    <td>
                      <label className="ed-toggle">
                        <input type="checkbox" role="switch" aria-label={`${p.name} active`} checked={p.active} onChange={() => void toggle(p)} />
                        <span className="ed-toggle-track" aria-hidden="true" />
                      </label>
                    </td>
                    <td className="q-row-actions">
                      <button type="button" className="link-button small" aria-label={`Edit ${p.name}`} onClick={() => setEditing(p)}>
                        <Pencil size={14} />
                      </button>
                      <button type="button" className="link-button small" aria-label={`Delete ${p.name}`} onClick={() => setDeleting(p)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="q-more">
            <button type="button" className="link-button small" onClick={exportCsv}>
              <Download size={14} aria-hidden="true" /> Export CSV
            </button>
            <button type="button" className="link-button small" onClick={() => void loadStarter()} disabled={busy}>
              <PackagePlus size={14} aria-hidden="true" /> Add any missing starter products
            </button>
          </div>
        </>
      )}

      {editing && (
        <ProductForm
          product={editing}
          onCancel={() => setEditing(null)}
          onSave={async (p) => {
            const saved = p.productId ? await api.updateProduct(p as Product) : await api.createProduct(p);
            setProducts((ps) => (p.productId ? (ps ?? []).map((x) => (x.productId === saved.productId ? saved : x)) : [...(ps ?? []), saved]));
            setEditing(null);
            setMessage({ tone: "success", text: `${saved.name} saved.` });
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.name}?`}
        confirmLabel="Delete"
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={() =>
          void (async () => {
            if (!deleting) return;
            setBusy(true);
            try {
              await api.deleteProduct(deleting.productId);
              setProducts((ps) => (ps ?? []).filter((x) => x.productId !== deleting.productId));
            } finally {
              setBusy(false);
              setDeleting(null);
            }
          })()
        }
      >
        <p>Packages that use it will skip it. Switching it off instead keeps it for later.</p>
      </ConfirmDialog>
    </div>
  );
}

function ProductForm({ product, onCancel, onSave }: { product: Partial<Product>; onCancel: () => void; onSave: (p: Partial<Product>) => Promise<void> }) {
  const [p, setP] = useState<Partial<Product>>(product);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof Product>(k: K, v: Product[K] | undefined) => setP((x) => ({ ...x, [k]: v }));
  const num = (v: string) => (v === "" ? undefined : Number(v));
  const isHp = p.category === "heat_pump";
  const isTank = p.category === "cylinder" || p.category === "buffer";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(p);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  const field = (k: keyof Product, lbl: string, type = "text", hint?: string) => (
    <div className="ed-field">
      <label className="ed-label" htmlFor={`pf-${String(k)}`}>
        {lbl}
      </label>
      <input
        id={`pf-${String(k)}`}
        className="input"
        type={type}
        step={type === "number" ? "any" : undefined}
        value={(p[k] as string | number | undefined) ?? ""}
        onChange={(e) => set(k, (type === "number" ? num(e.target.value) : e.target.value) as never)}
      />
      {hint && <p className="ed-hint">{hint}</p>}
    </div>
  );

  return (
    <div className="q-modal" role="dialog" aria-modal="true" aria-labelledby="pf-title">
      <form className="q-modal-card" onSubmit={(e) => void submit(e)}>
        <div className="q-modal-head">
          <h2 id="pf-title">{p.productId ? "Edit product" : "Add product"}</h2>
          <button type="button" className="link-button" aria-label="Close" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>
        {error && <div className="flash flash-danger">{error}</div>}
        <div className="q-form-grid">
          <div className="ed-field">
            <label className="ed-label" htmlFor="pf-category">
              Category
            </label>
            <select id="pf-category" className="input" value={p.category} onChange={(e) => set("category", e.target.value as Category)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </div>
          {field("name", "Name (shown on quotes)")}
          {field("brand", "Brand")}
          {field("model", "Range / model")}
          {field("sku", "Supplier code", "text", "Your merchant's product code: price list imports match on it.")}
          {field("mpn", "Manufacturer code")}
          {field("supplier", "Supplier")}
          {field("costExVat", "Your cost (£ ex VAT)", "number")}
          {isHp && field("kw", "Rated output (kW)", "number")}
          {isHp && field("designOutputKw", "Output at design temperature (kW)", "number", "From the datasheet at about −3 to −7 °C. If set, sizing uses it instead of the rated output.")}
          {isHp && field("scop35", "SCOP at 35 °C flow", "number")}
          {isHp && field("scop55", "SCOP at 55 °C flow", "number")}
          {isHp && field("refrigerant", "Refrigerant")}
          {isHp && (
            <div className="ed-field">
              <label className="ed-label" htmlFor="pf-phase">
                Electrical supply
              </label>
              <select id="pf-phase" className="input" value={p.phase ?? "single"} onChange={(e) => set("phase", e.target.value as "single" | "three")}>
                <option value="single">Single phase</option>
                <option value="three">Three phase</option>
              </select>
            </div>
          )}
          {isHp && field("soundPowerDb", "Sound power dB(A)", "number")}
          {isHp && field("dimensions", "Dimensions")}
          {isTank && field("litres", "Capacity (litres)", "number")}
          {field("datasheetUrl", "Datasheet link", "url")}
          {field("priceSourceUrl", "Price source link", "url")}
          {field("priceSeenAt", "Price date")}
        </div>
        <div className="ed-field">
          <label className="ed-label" htmlFor="pf-notes">
            Notes
          </label>
          <textarea id="pf-notes" className="input" rows={2} value={p.notes ?? ""} onChange={(e) => set("notes", e.target.value)} />
        </div>
        <div className="q-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save product"}
          </button>
        </div>
      </form>
    </div>
  );
}
